/**
 * Effective-dated settings (TMMPDM-0088, tmm-api 0153 section G items 24–28): one component for a
 * customer card's controls and the platform's AI controls (the card's tax went with tmm-api 0156,
 * TMMPDM-0091). A setting is a list of rows;
 * the current row is the latest one in effect, a future row can be withdrawn (softly: it moves to
 * History as Withdrawn), and a past row never changes. "Schedule a change…" sends a full row,
 * effective now (no `effectiveFrom`) or from a UTC instant (`…T00:00:00Z`). Every effective time is
 * shown in UTC as `YYYY-MM-DD HH:mm UTC`, so what is picked is what is seen.
 *
 * Each status shown comes from one field: Withdrawn from `withdrawnAt` alone. The bounds are checked
 * here before sending; the server's refusals land in the dialog that made the call, which stays open
 * (TMM-B4), and both dialogs keep one request in flight. A withdraw's 409 `ROW_IN_FORCE` (taking
 * effect within a minute) and `LAST_ROW` (the card's only row) have their own words (TMMPDM-0091).
 * TMMPDM-0098: `current` may be null (`emptyCurrent` replaces the Current list), and a field may be
 * a `select`. A parent that passes `supplied` owns the read. TMMPDM-0104: a select may name its
 * options (`optionLabels`, used in the dialog and the views alike), a field may start from
 * `initial` when there is no current row, and a field's `rejected` words a server 400 naming it.
 * TMMPDM-0105: a select's options may follow the dialog's other values (`optionsFor`, its value
 * moving when it drops out), a field may carry a hint and 400 words from those values (`hintFor`,
 * `rejectedFor`), and a select may show an `adornment` beside a value (options, Current, Scheduled).
 * TMMPDM-0106: a select option may be listed but disabled with its reason (`unavailable`), a field
 * may give way to a read-only line that holds Schedule off (`replacedFor`), a value may carry a
 * warning with a Schedule a change… link in Current and Scheduled (`warningFor`), and a 400's words
 * may land on another field (`rejectedFor` returning `{ key, text }`).
 * TMMPDM-0108: an enabled select option may carry a note (`optionNote`), and a field's error may
 * carry a button that fixes the cause in place (`errorActionFor`): it clears the error when it
 * works, and shows why in the error area when it doesn't.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  InputLabel,
  Link,
  Paper,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ApiError, errorCopy, isValidationFailed } from '@/api/errors';
import type { SettingRowMeta } from '@/api/tmm/types';
import {
  ConfirmDialog,
  EmptyState,
  ErrorState,
  SectionCard,
  SubmitButton,
} from '@/components/page';
import { HelpPopover } from '@/components/ui';
import { useCopy } from '@/hooks/useCopy';
import {
  NOT_SENT,
  formatMoney,
  formatNumber,
  formatPercentBp,
  formatUtcMinute as formatUtc,
} from '@/format';
import { StatusChip } from '@/status';
import {
  OMIT,
  inputValue,
  optionLabel,
  toWire,
  useFieldCheck,
  type FieldErrorAction,
  type SettingField,
} from './settingFields';

export type { SettingField } from './settingFields';

export type SettingRow = SettingRowMeta;

/**
 * Fields this list reads. A 0153 `SettingRowMeta` has `note` and the withdrawal fields required
 * and nullable, plus `seeded`. An assignment row (YAML `Row`) omits those when unset and has no
 * `seeded`. Both are accepted. What a present value shows does not change.
 */
export interface DatedSettingRow {
  id: number;
  effectiveFrom: string;
  createdAt: string;
  createdBy: string;
  note?: string | null;
  withdrawnAt?: string | null;
  withdrawnBy?: string | null;
  withdrawNote?: string | null;
}

/** A value's text with the field's adornment beside it, when it has one (TMMPDM-0105). */
function withAdornment(field: SettingField, value: unknown, text: ReactNode): ReactNode {
  if (!field.adornment || typeof value !== 'string' || value === '') return text;
  return (
    <Stack
      component="span"
      direction="row"
      spacing={1}
      sx={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap' }}
    >
      <span>{text}</span>
      {field.adornment(value)}
    </Stack>
  );
}

/** A row's field by its wire key. */
function valueOf(row: DatedSettingRow, key: string): unknown {
  return (row as unknown as Record<string, unknown>)[key];
}

export interface SettingsData<Row> {
  /** Null when the setting has no row in effect (a tier with no model). */
  current: Row | null;
  scheduled: Row[];
  history: Row[];
}

export interface EffectiveDatedSettingsProps<Row extends DatedSettingRow> {
  /** The h2, and the name the dialogs and tables use. */
  title: string;
  /** False leaves out the h2 where a tab already names the setting (TMMPDM-0103). Default true. */
  showTitle?: boolean;
  fields: SettingField[];
  /** For money fields. */
  currency?: string;
  /** Used when the component owns the read. Ignored while `supplied` is set. */
  load?: () => Promise<SettingsData<Row>>;
  /** Parent-owned envelope. The component does not read on mount. */
  supplied?: SettingsData<Row>;
  /** Re-read after a schedule or withdraw when the parent owns the envelope. */
  onReload?: () => void;
  schedule: (
    body: Record<string, unknown> & { effectiveFrom?: string; note?: string },
  ) => Promise<unknown>;
  withdraw: (rowId: number, note?: string) => Promise<void>;
  /** A banner above the lists (e.g. `reviewSuggested`). */
  banner?: ReactNode;
  /** One help line for the whole setting, beside the title. */
  help?: string;
  /** Shown in place of the Current list when `current` is null. */
  emptyCurrent?: string;
  /** Under the Current list, inside the Current card (a priced-currencies line). */
  afterCurrent?: ReactNode;
  /** False hides Schedule a change…. Default true. */
  allowSchedule?: boolean;
  /** Stable id when two blocks share a title. */
  instanceKey?: string;
}

function alignsLeft(kind: SettingField['kind']): boolean {
  return kind === 'text' || kind === 'select';
}

const NOTE_MAX = 500;
const DAY_MS = 86_400_000;

function tomorrowUtc(): string {
  return new Date(Date.now() + DAY_MS).toISOString().slice(0, 10);
}

/** A UTC date and time at or before now (read when the form is sent, never during render). */
function notLater(date: string, time: string): boolean {
  return Date.parse(`${date}T${time}:00Z`) <= Date.now();
}

function byStart(a: DatedSettingRow, b: DatedSettingRow): number {
  return Date.parse(a.effectiveFrom) - Date.parse(b.effectiveFrom);
}

function useFieldText() {
  const { i18n } = useTranslation();
  const locale = i18n.language;
  return (field: SettingField, value: unknown, currency: string | undefined): ReactNode => {
    if (value === null || value === undefined || value === '') return NOT_SENT;
    switch (field.kind) {
      case 'money':
        return currency && typeof value === 'number'
          ? formatMoney(value, currency, locale)
          : NOT_SENT;
      case 'integer':
        return typeof value === 'number' ? formatNumber(value, locale) : NOT_SENT;
      case 'percentBp':
        return typeof value === 'number' ? formatPercentBp(value, locale, 2) : NOT_SENT;
      case 'select':
        return typeof value === 'string' && value !== '' ? optionLabel(field, value) : NOT_SENT;
      default:
        return typeof value === 'string' ? value : NOT_SENT;
    }
  };
}

export function EffectiveDatedSettings<Row extends DatedSettingRow>({
  title,
  showTitle = true,
  fields,
  currency,
  load,
  supplied,
  onReload,
  schedule,
  withdraw,
  banner,
  help,
  emptyCurrent,
  afterCurrent,
  allowSchedule = true,
  instanceKey,
}: EffectiveDatedSettingsProps<Row>) {
  const { t } = useCopy();
  const fieldText = useFieldText();
  const owned = supplied === undefined;
  const [fetched, setFetched] = useState<SettingsData<Row> | null>(null);
  const [failure, setFailure] = useState<unknown>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [withdrawing, setWithdrawing] = useState<Row | null>(null);
  const data = owned ? fetched : supplied;
  // The latest loader, without re-reading on every render of a parent that passes a new closure.
  const loadRef = useRef(load);
  useEffect(() => {
    loadRef.current = load;
  });

  useEffect(() => {
    if (!owned) return;
    let cancelled = false;
    const read = loadRef.current;
    if (!read) return;
    read()
      .then((next) => {
        if (cancelled) return;
        setFetched(next);
        setFailure(null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setFailure(err ?? 'failed');
      });
    return () => {
      cancelled = true;
    };
  }, [reloadToken, owned]);

  const reread = () => {
    onReload?.();
    if (owned) setReloadToken((n) => n + 1);
  };
  const headingId = `settings-${(instanceKey ?? title).replace(/\W+/g, '-').toLowerCase()}`;

  /** A value with its adornment and, when the field has one for it, its warning (TMMPDM-0106). */
  const adorned = (field: SettingField, value: unknown, text: ReactNode): ReactNode => {
    const shown = withAdornment(field, value, text);
    const warning =
      typeof value === 'string' && value !== '' ? (field.warningFor?.(value) ?? null) : null;
    if (warning === null) return shown;
    return (
      <Stack spacing={0.5} sx={{ alignItems: 'flex-start' }}>
        <span>{shown}</span>
        <Alert severity="warning" sx={{ py: 0 }}>
          {warning}
          {allowSchedule ? (
            <>
              {' '}
              <Link
                component="button"
                type="button"
                variant="body2"
                sx={{ verticalAlign: 'baseline' }}
                onClick={() => setScheduleOpen(true)}
              >
                {t('datedSettings.schedule', 'Schedule a change…')}
              </Link>
            </>
          ) : null}
        </Alert>
      </Stack>
    );
  };

  const fieldHeads = fields.map((field) => (
    <TableCell key={field.key} align={alignsLeft(field.kind) ? 'left' : 'right'}>
      {field.label}
    </TableCell>
  ));
  const fieldCells = (row: Row, withExtras: boolean) =>
    fields.map((field) => (
      <TableCell
        key={field.key}
        align={alignsLeft(field.kind) ? 'left' : 'right'}
        sx={alignsLeft(field.kind) ? undefined : (theme) => ({ ...theme.typography.numeric })}
      >
        {withExtras
          ? adorned(
              field,
              valueOf(row, field.key),
              fieldText(field, valueOf(row, field.key), currency),
            )
          : fieldText(field, valueOf(row, field.key), currency)}
      </TableCell>
    ));

  let body: ReactNode;
  if (failure && !data) {
    body = (
      <ErrorState
        error={failure}
        message={t('datedSettings.loadError', 'Could not load these settings.')}
        onRetry={reread}
      />
    );
  } else if (!data) {
    body = (
      <Typography variant="body2" color="text.secondary">
        {t('common.loading', 'Loading')}
      </Typography>
    );
  } else {
    const scheduled = [...data.scheduled].sort(byStart);
    const history = [...data.history].sort((a, b) => byStart(b, a));
    const current = data.current;
    body = (
      <Stack spacing={2}>
        {failure ? (
          <ErrorState
            error={failure}
            message={t('datedSettings.loadError', 'Could not load these settings.')}
            onRetry={reread}
          />
        ) : null}
        <SectionCard
          headingLevel="h3"
          id={`${headingId}-current`}
          title={t('datedSettings.current', 'Current')}
        >
          {current === null ? (
            <Typography variant="body2">{emptyCurrent ?? NOT_SENT}</Typography>
          ) : (
            <>
              <Box
                component="dl"
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'max-content 1fr' },
                  columnGap: 3,
                  rowGap: 0.75,
                  m: 0,
                }}
              >
                {fields.map((field) => (
                  <DefinitionRow key={field.key} field={field}>
                    {adorned(
                      field,
                      valueOf(current, field.key),
                      fieldText(field, valueOf(current, field.key), currency),
                    )}
                  </DefinitionRow>
                ))}
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {t('datedSettings.inEffectSince', 'In effect since {{when}}', {
                  when: formatUtc(current.effectiveFrom),
                })}
                {' · '}
                {t('datedSettings.setBy', 'Set by {{who}}', { who: current.createdBy })}
              </Typography>
              {current.note ? (
                <Typography variant="body2" color="text.secondary">
                  {t('datedSettings.noteLine', 'Note: {{note}}', { note: current.note })}
                </Typography>
              ) : null}
            </>
          )}
          {afterCurrent}
        </SectionCard>

        <SectionCard
          headingLevel="h3"
          id={`${headingId}-scheduled`}
          title={t('datedSettings.scheduled', 'Scheduled')}
        >
          {scheduled.length === 0 ? (
            <EmptyState title={t('datedSettings.noneScheduled', 'No changes scheduled.')} />
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table
                size="small"
                aria-label={t('datedSettings.scheduledAria', '{{title}}: scheduled changes', {
                  title,
                })}
              >
                <TableHead>
                  <TableRow>
                    <TableCell>{t('datedSettings.col.starts', 'Starts')}</TableCell>
                    {fieldHeads}
                    <TableCell>{t('datedSettings.col.setBy', 'Set by')}</TableCell>
                    <TableCell>{t('datedSettings.col.note', 'Note')}</TableCell>
                    <TableCell>{t('datedSettings.col.actions', 'Actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {scheduled.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {formatUtc(row.effectiveFrom)}
                      </TableCell>
                      {fieldCells(row, true)}
                      <TableCell>{row.createdBy}</TableCell>
                      <TableCell>{row.note ?? NOT_SENT}</TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          onClick={() => setWithdrawing(row)}
                          aria-label={t(
                            'datedSettings.withdrawRow',
                            'Withdraw the change starting {{when}}',
                            {
                              when: formatUtc(row.effectiveFrom),
                            },
                          )}
                        >
                          {t('datedSettings.withdraw', 'Withdraw…')}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </SectionCard>

        <SectionCard
          headingLevel="h3"
          id={`${headingId}-history`}
          title={t('datedSettings.history', 'History')}
        >
          {history.length === 0 ? (
            <EmptyState title={t('datedSettings.noHistory', 'No earlier settings.')} />
          ) : (
            // History holds no control, so a narrow screen's scroll needs a focusable region (axe).
            <TableContainer
              component={Paper}
              variant="outlined"
              role="region"
              aria-labelledby={`${headingId}-history`}
              tabIndex={0}
            >
              <Table
                size="small"
                aria-label={t('datedSettings.historyAria', '{{title}}: history', { title })}
              >
                <TableHead>
                  <TableRow>
                    <TableCell>{t('datedSettings.col.starts', 'Starts')}</TableCell>
                    {fieldHeads}
                    <TableCell>{t('datedSettings.col.setBy', 'Set by')}</TableCell>
                    <TableCell>{t('datedSettings.col.note', 'Note')}</TableCell>
                    <TableCell>{t('datedSettings.col.status', 'Status')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {history.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {formatUtc(row.effectiveFrom)}
                      </TableCell>
                      {fieldCells(row, false)}
                      <TableCell>{row.createdBy}</TableCell>
                      <TableCell>{row.note ?? NOT_SENT}</TableCell>
                      <TableCell>
                        {row.withdrawnAt ? (
                          <Stack spacing={0.5} sx={{ alignItems: 'flex-start' }}>
                            <StatusChip
                              tone="neutral"
                              label={t('datedSettings.withdrawn', 'Withdrawn')}
                            />
                            {row.withdrawnBy ? (
                              <Typography variant="caption" color="text.secondary">
                                {t('datedSettings.withdrawnBy', 'by {{who}}', {
                                  who: row.withdrawnBy,
                                })}
                              </Typography>
                            ) : null}
                            {row.withdrawNote ? (
                              <Typography variant="caption" color="text.secondary">
                                {row.withdrawNote}
                              </Typography>
                            ) : null}
                          </Stack>
                        ) : (
                          t('datedSettings.past', 'Past')
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </SectionCard>
      </Stack>
    );
  }

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        sx={{ alignItems: { sm: 'center' }, justifyContent: 'space-between', mb: 2 }}
      >
        {showTitle ? (
          <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
            <Typography id={headingId} variant="h2" component="h2">
              {title}
            </Typography>
            {help ? <HelpPopover label={title} short={help} /> : null}
          </Stack>
        ) : (
          <span />
        )}
        <Button
          variant="outlined"
          disabled={!data || !allowSchedule}
          onClick={() => setScheduleOpen(true)}
          sx={{ alignSelf: { xs: 'flex-start', sm: 'auto' } }}
        >
          {t('datedSettings.schedule', 'Schedule a change…')}
        </Button>
      </Stack>
      {banner ? <Box sx={{ mb: 2 }}>{banner}</Box> : null}
      {body}
      {data ? (
        <ScheduleDialog
          open={scheduleOpen}
          title={title}
          fields={fields}
          currency={currency}
          current={data.current}
          schedule={schedule}
          onClose={() => setScheduleOpen(false)}
          onScheduled={() => {
            setScheduleOpen(false);
            reread();
          }}
          onReread={reread}
        />
      ) : null}
      <WithdrawDialog
        row={withdrawing}
        withdraw={withdraw}
        onClose={() => setWithdrawing(null)}
        onDone={() => {
          setWithdrawing(null);
          reread();
        }}
        onReread={reread}
      />
    </Box>
  );
}

function DefinitionRow({ field, children }: { field: SettingField; children: ReactNode }) {
  return (
    <>
      <Box component="dt" sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
        <Typography variant="body2" color="text.secondary" component="span">
          {field.label}
        </Typography>
        <HelpPopover label={field.label} short={field.help} />
      </Box>
      <Typography
        component="dd"
        variant="body2"
        sx={(theme) => ({
          m: 0,
          ...(alignsLeft(field.kind) ? {} : theme.typography.numeric),
        })}
      >
        {children}
      </Typography>
    </>
  );
}

interface ScheduleDialogProps {
  open: boolean;
  title: string;
  fields: SettingField[];
  currency: string | undefined;
  current: DatedSettingRow | null;
  schedule: EffectiveDatedSettingsProps<DatedSettingRow>['schedule'];
  onClose: () => void;
  onScheduled: () => void;
  onReread: () => void;
}

function ScheduleDialog({ open, title, ...rest }: ScheduleDialogProps) {
  const { t } = useCopy();
  const titleId = `schedule-${title.replace(/\W+/g, '-').toLowerCase()}`;
  return (
    <Dialog open={open} onClose={rest.onClose} fullWidth maxWidth="sm" aria-labelledby={titleId}>
      <DialogTitle id={titleId}>
        {t('datedSettings.scheduleTitle', 'Schedule a change: {{title}}', { title })}
      </DialogTitle>
      {open ? <ScheduleForm idPrefix={titleId} {...rest} /> : null}
    </Dialog>
  );
}

/**
 * The text a field starts with: the current row's value, else `initial`. A select whose current
 * value isn't one of its options also starts from `initial`, so the dialog never holds a value it
 * can't show (TMMPDM-0104). A current value that's `unavailable` starts empty: the person picks
 * one that can be chosen (TMMPDM-0106).
 */
function startValue(
  field: SettingField,
  current: DatedSettingRow | null,
  currency: string | undefined,
): string {
  if (!current) return field.initial ?? inputValue(field, undefined, currency);
  const value = inputValue(field, valueOf(current, field.key), currency);
  if (field.kind === 'select' && value !== '' && (field.unavailable?.(value) ?? null) !== null) {
    return '';
  }
  if (
    field.kind === 'select' &&
    field.initial !== undefined &&
    field.options !== undefined &&
    !field.options.includes(value)
  ) {
    return field.initial;
  }
  return value;
}

/** A field with its options taken from the dialog's values, when they follow them (TMMPDM-0105). */
function resolve(field: SettingField, values: Readonly<Record<string, string>>): SettingField {
  return field.optionsFor ? { ...field, options: field.optionsFor(values) } : field;
}

/**
 * The values with each following select settled on one of its options: its own value when among
 * them, else the current row's, else `initial`, else the first option (TMMPDM-0105).
 */
function settle(
  fields: SettingField[],
  values: Readonly<Record<string, string>>,
  current: DatedSettingRow | null,
  currency: string | undefined,
): Record<string, string> {
  const next = { ...values };
  for (const field of fields) {
    if (!field.optionsFor) continue;
    const options = field.optionsFor(next);
    if (options.includes(next[field.key] ?? '')) continue;
    const preferred = [
      current ? inputValue(field, valueOf(current, field.key), currency) : undefined,
      field.initial,
    ];
    next[field.key] =
      preferred.find((value) => value !== undefined && options.includes(value)) ?? options[0] ?? '';
  }
  return next;
}

function ScheduleForm({
  idPrefix,
  fields,
  currency,
  current,
  schedule,
  onClose,
  onScheduled,
  onReread,
}: Omit<ScheduleDialogProps, 'open' | 'title'> & { idPrefix: string }) {
  const { t } = useCopy();
  const check = useFieldCheck();
  const [values, setValues] = useState<Record<string, string>>(() =>
    settle(
      fields,
      Object.fromEntries(fields.map((field) => [field.key, startValue(field, current, currency)])),
      current,
      currency,
    ),
  );
  const shown = fields.map((field) => resolve(field, values));
  /** Each field's stand-in line while it's replaced (TMMPDM-0106); any one holds Schedule off. */
  const replaced = Object.fromEntries(
    fields.map((field) => [field.key, field.replacedFor?.(values) ?? null] as const),
  );
  const blocked = Object.values(replaced).some((line) => line !== null);
  const [when, setWhen] = useState<'now' | 'at'>('now');
  const [date, setDate] = useState(tomorrowUtc);
  const [time, setTime] = useState('00:00');
  const [note, setNote] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);
  /** An error action's own state (TMMPDM-0108): the field it is running on, and its refusal. */
  const [acting, setActing] = useState<string | null>(null);
  const [actionMessages, setActionMessages] = useState<Record<string, string>>({});

  const runAction = async (key: string, action: FieldErrorAction) => {
    if (acting !== null) return;
    setActing(key);
    setActionMessages(({ [key]: _dropped, ...others }) => others);
    try {
      const message = await action.run();
      if (message === null) setErrors(({ [key]: _dropped, ...others }) => others);
      else setActionMessages((prev) => ({ ...prev, [key]: message }));
    } finally {
      setActing(null);
    }
  };

  const validate = (): Record<string, string> => {
    const next: Record<string, string> = {};
    for (const field of shown) {
      const message = check(field, values[field.key] ?? '', currency);
      if (message) next[field.key] = message;
    }
    if (when === 'at' && (!/^\d{4}-\d{2}-\d{2}$/.test(date) || !/^\d{2}:\d{2}$/.test(time))) {
      next.effectiveFrom = t('datedSettings.form.whenRequired', 'Choose a date and a time.');
    } else if (when === 'at' && notLater(date, time)) {
      next.effectiveFrom = t(
        'datedSettings.errors.notLater',
        'Choose a time after now (UTC), or pick Now.',
      );
    }
    if (note.trim().length > NOTE_MAX) {
      next.note = t('datedSettings.form.tooLong', 'Use at most {{max, number}} characters.', {
        max: NOTE_MAX,
      });
    }
    return next;
  };

  const submit = async () => {
    if (inFlight.current || blocked || acting !== null) return;
    const next = validate();
    setErrors(next);
    setFormError(null);
    if (Object.keys(next).length > 0) return;
    const body: Record<string, unknown> & { effectiveFrom?: string; note?: string } =
      Object.fromEntries(
        fields
          .map((field) => [field.key, toWire(field, values[field.key] ?? '', currency)] as const)
          .filter(([, wire]) => wire !== OMIT),
      );
    if (when === 'at') body.effectiveFrom = `${date}T${time}:00Z`;
    if (note.trim() !== '') body.note = note.trim();
    inFlight.current = true;
    setBusy(true);
    try {
      await schedule(body);
      onScheduled();
    } catch (err) {
      const code = err instanceof ApiError ? String(err.code) : '';
      if (code === 'EFFECTIVE_FROM_TAKEN') {
        setErrors({
          effectiveFrom: t(
            'datedSettings.errors.taken',
            'Another change already starts at that time. Pick another time.',
          ),
        });
      } else if (code === 'MODEL_NOT_PRICED' && err instanceof ApiError) {
        const raw = err.details.backend?.['missingCurrencies'];
        const currencies = Array.isArray(raw)
          ? raw.filter((item): item is string => typeof item === 'string')
          : [];
        const model = (values.model ?? '').trim();
        setFormError(
          t(
            'ai.models.notPriced',
            '{{model}} has no price in force at that time in {{currencies}}. Add the price first, then schedule again.',
            { model: model === '' ? NOT_SENT : model, currencies: currencies.join(', ') },
          ),
        );
      } else if (err instanceof ApiError && isValidationFailed(err)) {
        const sent = err.details.backend?.['fields'];
        const named = sent && typeof sent === 'object' ? Object.keys(sent) : [];
        const mapped: Record<string, string> = {};
        for (const key of named) {
          if (key === 'effectiveFrom') {
            mapped.effectiveFrom = t(
              'datedSettings.errors.future',
              'Must be in the future. Pick Now to apply it straight away.',
            );
          } else if (
            fields.some(
              (field) =>
                field.key === key &&
                (field.rejectedFor !== undefined || field.rejected !== undefined),
            )
          ) {
            const field = fields.find((candidate) => candidate.key === key);
            const words = field?.rejectedFor?.(values) ?? field?.rejected ?? '';
            if (typeof words === 'string') mapped[key] = words;
            else mapped[words.key] = words.text;
          } else if (key === 'note' || fields.some((field) => field.key === key)) {
            mapped[key] = t(
              'errors.field.rejected',
              "This value isn't accepted. Check it and try again.",
            );
          }
        }
        setErrors(mapped);
        if (Object.keys(mapped).length === 0) {
          setFormError(
            errorCopy(err, t, t('datedSettings.errors.schedule', "Couldn't schedule this change.")),
          );
        }
      } else if (code === 'RATE_CARD_NOT_FOUND' || code === 'NOT_FOUND') {
        setFormError(
          errorCopy(err, t, t('datedSettings.errors.notFound', "That setting couldn't be found.")),
        );
        onReread();
      } else {
        setFormError(
          errorCopy(err, t, t('datedSettings.errors.schedule', "Couldn't schedule this change.")),
        );
      }
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  const action = (field: SettingField) => field.errorActionFor?.(values) ?? null;
  const whenLabelId = `${idPrefix}-when`;
  return (
    <>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {formError ? <Alert severity="error">{formError}</Alert> : null}
          {shown.map((field) =>
            replaced[field.key] !== null ? (
              <Box key={field.key}>
                <Typography variant="body2" color="text.secondary">
                  {field.label}
                </Typography>
                <Typography variant="body2">{replaced[field.key]}</Typography>
              </Box>
            ) : field.kind === 'select' ? (
              <FormControl key={field.key} fullWidth required error={Boolean(errors[field.key])}>
                <InputLabel id={`${idPrefix}-${field.key}-label`}>{field.label}</InputLabel>
                <Select
                  labelId={`${idPrefix}-${field.key}-label`}
                  id={`${idPrefix}-${field.key}`}
                  label={field.label}
                  value={values[field.key] ?? ''}
                  onChange={(event) => {
                    const next = settle(
                      fields,
                      { ...values, [field.key]: event.target.value },
                      current,
                      currency,
                    );
                    setValues(next);
                    setActionMessages(({ [field.key]: _dropped, ...others }) => others);
                    // A following select that moved drops its error too.
                    setErrors((prev) =>
                      Object.fromEntries(
                        Object.entries(prev).filter(
                          ([key]) => key !== field.key && next[key] === values[key],
                        ),
                      ),
                    );
                  }}
                >
                  {(values[field.key] ?? '') === '' ? (
                    <MenuItem value="" sx={{ display: 'none' }} />
                  ) : null}
                  {(field.options ?? []).map((option) => {
                    const reason = field.unavailable?.(option) ?? null;
                    const note = reason ?? field.optionNote?.(option) ?? null;
                    return (
                      <MenuItem key={option} value={option} disabled={reason !== null}>
                        {note === null ? (
                          withAdornment(field, option, optionLabel(field, option))
                        ) : (
                          <Stack component="span">
                            {withAdornment(field, option, optionLabel(field, option))}
                            <Typography
                              component="span"
                              variant="caption"
                              sx={{ whiteSpace: 'normal' }}
                            >
                              {note}
                            </Typography>
                          </Stack>
                        )}
                      </MenuItem>
                    );
                  })}
                </Select>
                <FormHelperText>
                  {errors[field.key]
                    ? (actionMessages[field.key] ?? errors[field.key])
                    : field.help}
                </FormHelperText>
                {errors[field.key] && action(field) ? (
                  <Box>
                    <Button
                      size="small"
                      {...(acting === field.key
                        ? {}
                        : { 'aria-label': action(field)?.ariaLabel ?? '' })}
                      disabled={acting !== null || busy}
                      onClick={() => {
                        const found = action(field);
                        if (found) void runAction(field.key, found);
                      }}
                    >
                      {acting === field.key ? action(field)?.busyLabel : action(field)?.label}
                    </Button>
                  </Box>
                ) : null}
                {field.hintFor?.(values) ? (
                  <FormHelperText component="div" error={false}>
                    {field.hintFor(values)}
                  </FormHelperText>
                ) : null}
              </FormControl>
            ) : (
              <TextField
                key={field.key}
                id={`${idPrefix}-${field.key}`}
                label={field.label}
                value={values[field.key] ?? ''}
                onChange={(event) => {
                  const value = event.target.value;
                  setValues((prev) => ({ ...prev, [field.key]: value }));
                  setErrors(({ [field.key]: _dropped, ...others }) => others);
                }}
                error={Boolean(errors[field.key])}
                helperText={errors[field.key] ?? field.help}
                required={field.kind !== 'text' || !field.nullable}
                slotProps={{
                  htmlInput: {
                    inputMode:
                      field.kind === 'text'
                        ? 'text'
                        : field.kind === 'integer'
                          ? 'numeric'
                          : 'decimal',
                  },
                }}
              />
            ),
          )}
          <FormControl error={Boolean(errors.effectiveFrom)}>
            <FormLabel id={whenLabelId}>{t('datedSettings.form.when', 'When')}</FormLabel>
            <RadioGroup
              aria-labelledby={whenLabelId}
              value={when}
              onChange={(event) => {
                setWhen(event.target.value === 'at' ? 'at' : 'now');
                setErrors(({ effectiveFrom: _dropped, ...others }) => others);
              }}
            >
              <FormControlLabel
                value="now"
                control={<Radio />}
                label={t('datedSettings.form.now', 'Now')}
              />
              <FormControlLabel
                value="at"
                control={<Radio />}
                label={t('datedSettings.form.at', 'From a date and time')}
              />
            </RadioGroup>
          </FormControl>
          {when === 'at' ? (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                id={`${idPrefix}-date`}
                type="date"
                label={t('datedSettings.form.date', 'Date (UTC)')}
                value={date}
                onChange={(event) => {
                  setDate(event.target.value);
                  setErrors(({ effectiveFrom: _dropped, ...others }) => others);
                }}
                error={Boolean(errors.effectiveFrom)}
                slotProps={{ inputLabel: { shrink: true } }}
              />
              <TextField
                id={`${idPrefix}-time`}
                type="time"
                label={t('datedSettings.form.time', 'Time (UTC)')}
                value={time}
                onChange={(event) => {
                  setTime(event.target.value);
                  setErrors(({ effectiveFrom: _dropped, ...others }) => others);
                }}
                error={Boolean(errors.effectiveFrom)}
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </Stack>
          ) : null}
          {errors.effectiveFrom ? (
            <Typography role="alert" variant="body2" color="error">
              {errors.effectiveFrom}
            </Typography>
          ) : null}
          <TextField
            id={`${idPrefix}-note`}
            label={t('datedSettings.form.note', 'Note (optional)')}
            value={note}
            onChange={(event) => {
              setNote(event.target.value);
              setErrors(({ note: _dropped, ...others }) => others);
            }}
            error={Boolean(errors.note)}
            helperText={errors.note}
            multiline
            minRows={2}
            slotProps={{ htmlInput: { maxLength: NOTE_MAX } }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy || acting !== null}>
          {t('common.cancel', 'Cancel')}
        </Button>
        <SubmitButton
          busy={busy}
          disabled={blocked || acting !== null}
          onClick={() => void submit()}
        >
          {t('datedSettings.form.submit', 'Schedule')}
        </SubmitButton>
      </DialogActions>
    </>
  );
}

function WithdrawDialog<Row extends DatedSettingRow>({
  row,
  withdraw,
  onClose,
  onDone,
  onReread,
}: {
  row: Row | null;
  withdraw: (rowId: number, note?: string) => Promise<void>;
  onClose: () => void;
  onDone: () => void;
  onReread: () => void;
}) {
  const { t } = useCopy();
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);
  const [openedFor, setOpenedFor] = useState<number | null>(null);
  // A new opening starts with an empty note and no old error.
  if (!row && openedFor !== null) setOpenedFor(null);
  if (row && row.id !== openedFor) {
    setOpenedFor(row.id);
    setNote('');
    setError(null);
  }

  const confirm = async () => {
    if (!row || inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setError(null);
    try {
      const trimmed = note.trim();
      await withdraw(row.id, trimmed === '' ? undefined : trimmed);
      onDone();
    } catch (err) {
      const code = err instanceof ApiError ? String(err.code) : '';
      if (code === 'SETTING_IN_EFFECT') {
        setError(
          t(
            'datedSettings.errors.inEffect',
            "This change is already in effect, so it can't be withdrawn. The list has been reloaded.",
          ),
        );
        onReread();
      } else if (code === 'ROW_IN_FORCE') {
        setError(
          t(
            'datedSettings.errors.rowInForce',
            "This setting is already in force and can't be withdrawn. Schedule a new value instead.",
          ),
        );
        onReread();
      } else if (code === 'LAST_ROW') {
        setError(
          t(
            'datedSettings.errors.lastRow',
            "This is the only setting row, so it can't be withdrawn.",
          ),
        );
      } else if (code === 'RATE_CARD_NOT_FOUND' || code === 'NOT_FOUND') {
        setError(
          errorCopy(err, t, t('datedSettings.errors.notFound', "That setting couldn't be found.")),
        );
        onReread();
      } else {
        setError(
          errorCopy(err, t, t('datedSettings.errors.withdraw', "Couldn't withdraw this change.")),
        );
      }
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  const when = row ? formatUtc(row.effectiveFrom) : '';
  return (
    <ConfirmDialog
      open={row !== null}
      title={t('datedSettings.withdrawTitle', 'Withdraw a scheduled change')}
      consequence={
        <Stack spacing={2}>
          <span>
            {t('datedSettings.withdrawConsequence', 'Withdraw the change starting {{when}}?', {
              when,
            })}
          </span>
          <TextField
            label={t('datedSettings.form.note', 'Note (optional)')}
            value={note}
            onChange={(event) => setNote(event.target.value)}
            multiline
            minRows={2}
            slotProps={{ htmlInput: { maxLength: NOTE_MAX } }}
          />
        </Stack>
      }
      confirmLabel={t('datedSettings.withdrawConfirm', 'Withdraw')}
      destructive
      busy={busy}
      error={error}
      onConfirm={() => void confirm()}
      onCancel={onClose}
    />
  );
}
